import * as Sentry from '@sentry/react';

import { getEnvVar } from '@/shared/utils/envVarConfig';

/**
 * Initialize Sentry early to catch errors from app startup.
 * Called as first import in main.tsx.
 */
export function initSentry() {
  const dsn = getEnvVar('VITE_SENTRY_DSN');

  // Skip initialization if no DSN configured
  if (!dsn) {
    // eslint-disable-next-line no-console
    console.info('[Sentry] No DSN configured - error monitoring disabled');
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment: getEnvVar('VITE_SENTRY_ENVIRONMENT') || 'dev',
      release: getEnvVar('VITE_APP_VERSION'),

      // Performance monitoring - sample 10% in prod, 100% in dev
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

      // Only capture errors from duos domains, Cloudflare previews, and localhost
      allowUrls: [
        /https?:\/\/([a-z0-9-]+\.)?duos\.gankster\.gg/,
        /https?:\/\/([a-z0-9-]+\.)?duos-frontend\.pages\.dev/,
        /https?:\/\/localhost/,
      ],

      // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/14
      ignoreErrors: [
        'Non-Error promise rejection captured with value: Object Not Found Matching Id',
        // Browser extensions modifying DOM cause React reconciliation failures
        /Failed to execute 'removeChild' on 'Node'/,
        // ViewTransition API fails when user switches tabs during navigation
        /Skipped ViewTransition due to document being hidden/,
      ],

      // Enable PII collection (IP, user agent) for user context and debugging
      sendDefaultPii: true,
    });

    // eslint-disable-next-line no-console
    console.info(
      '[Sentry] Initialized for environment:',
      getEnvVar('VITE_SENTRY_ENVIRONMENT') || 'dev',
    );
  } catch (error) {
    // Log failure but don't crash the app - better to run without Sentry than not at all
    // eslint-disable-next-line no-console
    console.error('[Sentry] Failed to initialize:', error);
  }
}

// Re-export Sentry for direct usage
export { Sentry };
